import { Action, Reducer } from "redux";
import { produce } from "immer";
import _ from "underscore";
import { AssociationType } from "avvir";

import CloudFile from "../../../models/domain/cloud_file";
import FileInformationConverter from "../../converters/file_information_converter";
import Floor from "../../../models/domain/floor";
import makeUpdateModel from "./make_update_model";
import without from "../../utilities/general/without";
import { DEFAULT_SCAN_DATASET_FOR_FLOOR_UPDATED, DefaultScanDatasetForFloorUpdatedEvent } from "../../../events/superadmin/scan_datasets/default_scan_dataset_for_floor_updated";
import { ELEMENT_ACTION_RECORDED, ElementActionRecordedEvent } from "../../../events/viewer/element_action_recorded";
import { ELEMENT_ACTIONS_LOADED, ElementActionsLoadedEvent } from "../../../events/loaded/element_actions_loaded";
import { FILE_ASSOCIATIONS_UPDATED, FileAssociationsUpdatedEvent } from "../../../events/files/file_associations_updated";
import { FLOOR_CREATED, FloorCreatedEvent } from "../../../events/superadmin/floors/floor_created";
import { FLOOR_DELETED, FloorDeletedEvent } from "../../../events/superadmin/floors/floor_deleted";
import { FLOOR_FILE_UPLOADED, FloorFileUploadedEvent } from "../../../events/files/upload/floor_file_uploaded";
import { FLOOR_FILES_FOR_PROJECT_LOADED, FloorFilesForProjectLoadedEvent } from "../../../events/loaded/floors/floor_files_for_project_loaded";
import { FLOOR_FILES_LOADED, FloorFilesLoadedEvent } from "../../../events/loaded/floors/floor_files_loaded";
import { FLOOR_BACKUPS_LOADED, FloorBackupsLoadedEvent } from "../../../events/loaded/floors/floor_backups_loaded";
import { FLOOR_LOADED, FloorLoadedEvent } from "../../../events/loaded/floors/floor_loaded";
import { FLOOR_MASTERFORMAT_PROGRESS_LOADED, FloorMasterformatProgressLoadedEvent } from "../../../events/loaded/floors/floor_masterformat_progress_loaded";
import { FLOOR_ORDER_UPDATED, FloorOrderUpdatedEvent } from "../../../events/superadmin/floors/floor_order_updated";
import { FLOOR_UPDATED, FloorUpdatedEvent } from "../../../events/superadmin/floors/floor_updated";
import { FloorPurposeType } from "../../../models/domain/enums/purpose_type";
import { FLOORS_FOR_PROJECT_LOADED, FloorsForProjectLoadedEvent } from "../../../events/loaded/floors/floors_for_project_loaded";
import { OBSTRUCTIONS_CREATED, ObstructionsCreatedEvent } from "../../../events/viewer/obstructions_created";
import { OBSTRUCTIONS_DELETED, ObstructionsDeletedEvent } from "../../../events/viewer/obstructions_deleted";
import { OBSTRUCTIONS_LOADED, ObstructionsLoadedEvent } from "../../../events/loaded/building_elements/obstructions_loaded";
import { PHOTO_AREA_MINIMAP_TO_BIM_MINIMAP_TRANSFORM_SAVED, PhotoAreaMinimapToBimMinimapTransformSavedEvent } from "../../../events/photos/photo_area_minimap_to_bim_minimap_transform_saved";
import { PLANNED_BUILDING_ELEMENTS_LOADED, PlannedBuildingElementsLoadedEvent } from "../../../events/loaded/building_elements/planned_building_elements_loaded";
import { SCAN_DATASET_CREATED, ScanDatasetCreatedEvent } from "../../../events/superadmin/scan_datasets/scan_dataset_created";
import { SCAN_DATASET_DELETED, ScanDatasetDeletedEvent } from "../../../events/superadmin/scan_datasets/scan_dataset_deleted";
import { SCAN_DATASETS_CREATED_FROM_TSV, ScanDatasetsCreatedFromTsvEvent } from "../../../events/superadmin/scan_datasets/scan_datasets_created_from_tsv";
import { SCAN_DATASETS_FOR_PROJECT_LOADED, ScanDatasetsForProjectLoadedEvent } from "../../../events/loaded/scan_datasets/scan_datasets_for_project_loaded";
import { isBigBimViewerPerformanceOn } from "../../utilities/big_bim_viewer_performance";

type FloorEvents =
  | DefaultScanDatasetForFloorUpdatedEvent
  | FloorCreatedEvent
  | FloorDeletedEvent
  | FloorFilesLoadedEvent
  | FloorFilesForProjectLoadedEvent
  | FloorFileUploadedEvent
  | FloorLoadedEvent
  | FloorsForProjectLoadedEvent
  | FloorUpdatedEvent
  | PhotoAreaMinimapToBimMinimapTransformSavedEvent
  | ElementActionsLoadedEvent
  | ElementActionRecordedEvent
  | ScanDatasetCreatedEvent
  | ScanDatasetDeletedEvent
  | ScanDatasetsCreatedFromTsvEvent
  | FloorOrderUpdatedEvent
  | FileAssociationsUpdatedEvent
  | Action<"load_floor_for_viewer_started">
  | Action<"load_floor_for_viewer_done">
  | ObstructionsLoadedEvent
  | ObstructionsCreatedEvent
  | ObstructionsDeletedEvent
  | FloorMasterformatProgressLoadedEvent
  | PlannedBuildingElementsLoadedEvent
  | ScanDatasetsForProjectLoadedEvent
  | FloorBackupsLoadedEvent

export interface FloorsStore {
  byFirebaseId: { [firebaseId: string]: Floor };
  floorForViewerLoading?: boolean;
  loadedFloorForViewerFirebaseId?: string;
}

const addFileToFloor = (floors: FloorsStore, floorId: string, file: CloudFile) => updateFloor(floors, floorId, floor => produce(floor, (floorDraft) => {
  if (file.purposeType === FloorPurposeType.OTHER) {
    floorDraft.files[file.purposeType] = _.union(floor.files?.[file.purposeType], [file.id]);
  } else {
    floorDraft.files[file.purposeType] = [file.id];
  }
}));

const removeFileFromFloor = (floors: FloorsStore, floorId: string, file: CloudFile) => updateFloor(floors, floorId, floor => produce(floor, (floorDraft) => {
  floorDraft.files[file.purposeType] = without(floorDraft.files?.[file.purposeType], file.id);
}));

const emptyFloor = new Floor();

const updateFloor = makeUpdateModel<Floor>(emptyFloor);

const removeFloor = (floors: FloorsStore, floorFirebaseId: string): FloorsStore => {
  return produce(floors, (floorsDraft) => {
    delete floorsDraft.byFirebaseId[floorFirebaseId];
  });
};

const reduceFloors: Reducer<FloorsStore, FloorEvents> = (floors: FloorsStore = { byFirebaseId: {} }, event) => {
  if (!floors?.byFirebaseId) {
    floors = { byFirebaseId: {} };
  }
  switch (event.type) {
    case FLOOR_CREATED: {
      const addedFloor = new Floor(event.payload);
      return updateFloor(floors, addedFloor.firebaseId, addedFloor);
    }
    case PHOTO_AREA_MINIMAP_TO_BIM_MINIMAP_TRANSFORM_SAVED: {
      return updateFloor(floors, event.payload.floor.firebaseId, event.payload.floor);
    }
    case FLOOR_UPDATED: {
      return updateFloor(floors, event.payload.floor.firebaseId, event.payload.floor);
    }
    case FLOOR_DELETED: {
      return removeFloor(floors, event.payload.floorId);
    }
    case FLOOR_FILES_LOADED: {
      return updateFloor(floors, event.payload.floorId, {
        files: FileInformationConverter.fileIdsFromApi(event.payload.files)
      });
    }
    case FLOOR_BACKUPS_LOADED: {
      return updateFloor(floors, event.payload.floorId, {
        backups: event.payload.backups
      });
    }
    case FLOOR_FILES_FOR_PROJECT_LOADED: {
      return event.payload.reduce((floorsSoFar, { floorId, files }) => {
        return updateFloor(floorsSoFar, floorId, (originalFloor) => ({
          backups: [
            ...originalFloor.backups,
          ],
          files: {
            ...originalFloor.files,
            ...files.reduce((filesSoFar, apiFile) => {
              const file = new CloudFile(apiFile);
              if (filesSoFar[file.purposeType]) {
                filesSoFar[file.purposeType] = _.union(filesSoFar[file.purposeType], [file.id]);
              } else {
                filesSoFar[file.purposeType] = [file.id];
              }
              return filesSoFar;
            }, {})
          }
        }));
      }, floors);
    }
    case FILE_ASSOCIATIONS_UPDATED: {
      const previousFloorFiles = event.payload.previousFiles.filter((file) => file.associationType === AssociationType.FLOOR);
      const floorFiles = event.payload.files.filter((file) => file.associationType === AssociationType.FLOOR);
      const idMap = {};

      if (floorFiles.length === 0 && previousFloorFiles.length === 0) {
        return floors;
      }

      let updatedStore = floors;

      const updateFloorIdMap = (file) => {
        let floorId = idMap[file.associationId];
        if (floorId == null) {
          const foundFloor = Object.values(updatedStore.byFirebaseId).find((floor) => floor.id === file.associationId);
          if (foundFloor != null) {
            idMap[file.associationId] = foundFloor.firebaseId;
          }
        }
      };

      previousFloorFiles.forEach(updateFloorIdMap);
      floorFiles.forEach(updateFloorIdMap);

      previousFloorFiles.forEach((previousFile) => {
        const floorId = idMap[previousFile.associationId];
        if (floorId != null) {
          updatedStore = removeFileFromFloor(updatedStore, floorId, new CloudFile(previousFile));
        }
      });

      floorFiles.forEach((file) => {
        const floorId = idMap[file.associationId];
        if (floorId != null) {
          updatedStore = addFileToFloor(updatedStore, floorId, new CloudFile(file));
        }
      });

      return { ...updatedStore };
    }
    case FLOOR_FILE_UPLOADED: {
      return addFileToFloor(floors, event.payload.floorId, new CloudFile(event.payload.file));
    }
    case DEFAULT_SCAN_DATASET_FOR_FLOOR_UPDATED: {
      return updateFloor(floors, event.payload.floorId, {
        defaultFirebaseScanDatasetId: event.payload.scanDatasetId,
      });
    }
    case SCAN_DATASET_CREATED: {
      return updateFloor(floors, event.payload.firebaseFloorId, (floor) => ({
        firebaseScanDatasetIds: _.union(floor.firebaseScanDatasetIds, [event.payload.firebaseId])
      }));
    }
    case SCAN_DATASETS_CREATED_FROM_TSV: {
      return event.payload.reduce((floorsSoFar, scanDataset) => {
        return updateFloor(floorsSoFar, scanDataset.firebaseFloorId, (floor) => ({
          firebaseScanDatasetIds: _.union(floor.firebaseScanDatasetIds, [scanDataset.firebaseId])
        }));
      }, floors);
    }
    case SCAN_DATASET_DELETED: {
      return updateFloor(floors, event.payload.floorId, (floor) => {
        return {
          ...floor,
          defaultFirebaseScanDatasetId: event.payload.newDefaultScanDatasetId || floor.defaultFirebaseScanDatasetId,
          firebaseScanDatasetIds: without(floor.firebaseScanDatasetIds, event.payload.scanDatasetId)
        };
      });
    }
    case SCAN_DATASETS_FOR_PROJECT_LOADED: {
      return event.payload.scanDatasets.reduce((floorsSoFar, scanDataset) => {
        return updateFloor(floorsSoFar, scanDataset.firebaseFloorId, (floor) => {
          return {
            firebaseProjectId: event.payload.projectId,
            firebaseScanDatasetIds: _.union(floor.firebaseScanDatasetIds, [scanDataset.firebaseId])
          };
        });
      }, floors);
    }
    case "load_floor_for_viewer_started": {
      return {
        ...floors,
        floorForViewerLoading: true
      };
    }
    case "load_floor_for_viewer_done": {
      return {
        ...floors,
        floorForViewerLoading: false,
      };
    }
    case FLOOR_LOADED: {
      const loadedFloor = new Floor(event.payload.floor);
      const previousFloor = floors.byFirebaseId[loadedFloor.firebaseId];
      const bigBimViewerPerformance = isBigBimViewerPerformanceOn();
      const updatedFloors = updateFloor(floors, loadedFloor.firebaseId, (currentFloor) => ({
        ...currentFloor,
        ...loadedFloor,
        // The API floor carries neither, so Floor defaults them to {}; keep what was loaded separately.
        ...(bigBimViewerPerformance ? {
          elementActionsByGlobalId: currentFloor?.elementActionsByGlobalId ?? loadedFloor.elementActionsByGlobalId,
          elementTradeCodesInBim: currentFloor?.elementTradeCodesInBim ?? loadedFloor.elementTradeCodesInBim
        } : {}),
        offset: currentFloor?.offset?.equals(loadedFloor.offset) ? currentFloor.offset : loadedFloor.offset,
        backups: [ ...currentFloor.backups ],
        files: {
          ...currentFloor.files,
          ...loadedFloor.files
        },
        obstructions: loadedFloor.obstructions ? {
          ...currentFloor?.obstructions,
          ...loadedFloor.obstructions,
          byObstructingElementGlobalId: {
            ...currentFloor?.obstructions?.byObstructingElementGlobalId,
            ...loadedFloor.obstructions.byObstructingElementGlobalId
          }
        } : currentFloor?.obstructions,
        photoAreaMinimapPixelToBimMinimapPixel: currentFloor.photoAreaMinimapPixelToBimMinimapPixel ?? loadedFloor.photoAreaMinimapPixelToBimMinimapPixel,
        masterformatProgress: { ...currentFloor.masterformatProgress }
      }));
      // The floor is polled every few seconds; one that hasn't changed keeps its object, so nothing
      // reading it re-renders.
      return bigBimViewerPerformance && previousFloor && _.isEqual(updatedFloors.byFirebaseId[loadedFloor.firebaseId], previousFloor) ? floors : updatedFloors;
    }
    case FLOORS_FOR_PROJECT_LOADED: {
      return event.payload.reduce((floorsSoFar, floor) => {
        const loadedFloor = new Floor(floor);
        return updateFloor(floorsSoFar, floor.firebaseId, (currentFloor) => ({
          ...currentFloor,
          ...loadedFloor,
          backups: [ ...currentFloor.backups ],
          files: {
            ...currentFloor.files,
            ...loadedFloor.files
          },
          obstructions: {
            ...currentFloor.obstructions,
            ...loadedFloor.obstructions
          },
          masterformatProgress: { ...currentFloor.masterformatProgress}
        }));
      }, { ...floors });
    }
    case ELEMENT_ACTIONS_LOADED: {
      return updateFloor(floors, event.payload.floorId, (currentFloor) => {
        if (currentFloor) {
          return {
            elementActionsByGlobalId: {
              ...currentFloor.elementActionsByGlobalId,
              [event.payload.globalId]: event.payload.actionHistory
            }
          };
        }

        return currentFloor;
      });
    }
    case ELEMENT_ACTION_RECORDED: {
      return updateFloor(floors, event.payload.floorId, (currentFloor) => {
        if (currentFloor) {
          const existingActions = currentFloor.elementActionsByGlobalId[event.payload.globalId];
          const elementActions = existingActions == null ? [] : existingActions.slice();
          elementActions.unshift(event.payload.action);

          return {
            elementActionsByGlobalId: {
              ...currentFloor.elementActionsByGlobalId,
              [event.payload.globalId]: elementActions
            }
          };
        }

        return currentFloor;
      });
    }
    case FLOOR_ORDER_UPDATED: {
      return event.payload.floorIds.reduce((floorsSoFar, floorId, currentIndex) => {
        return updateFloor(floorsSoFar, floorId, { ordinal: currentIndex + 1 });
      }, { ...floors });
    }
    case OBSTRUCTIONS_LOADED: {
      if (!isBigBimViewerPerformanceOn()) {
        return updateFloor(floors, event.payload.firebaseFloorId, (floor) => {
          const newObstructionsByGlobalId = event.payload.obstructingElementGlobalIds.reduce((obstructionsSoFar, globalId) => {
            return {
              ...obstructionsSoFar,
              [globalId]: event.payload.obstructions[globalId] || null
            };
          }, floor?.obstructions?.byObstructingElementGlobalId);
          return new Floor({
            ...floor,
            obstructions: {
              byObstructingElementGlobalId: newObstructionsByGlobalId
            }
          });
        });
      }
      return updateFloor(floors, event.payload.firebaseFloorId, (floor) => {
        // One copy, filled in place: spreading it again for every element made a big selection quadratic.
        const byObstructingElementGlobalId = { ...floor?.obstructions?.byObstructingElementGlobalId };
        for (const globalId of event.payload.obstructingElementGlobalIds) {
          byObstructingElementGlobalId[globalId] = event.payload.obstructions[globalId] || null;
        }
        return { obstructions: { byObstructingElementGlobalId } };
      });
    }
    case OBSTRUCTIONS_CREATED: {
      return updateFloor(floors, event.payload.floorId, (floor) => {
        return new Floor({
          ...floor,
          obstructions: {
            byObstructingElementGlobalId: {
              ...floor?.obstructions?.byObstructingElementGlobalId,
              ...event.payload.obstructions
            }
          }
        });
      });
    }
    case OBSTRUCTIONS_DELETED: {
      return updateFloor(floors, event.payload.floorId, (floor) => {
        return new Floor({
          ...floor,
          obstructions: {
            byObstructingElementGlobalId: {
              ...floor?.obstructions?.byObstructingElementGlobalId,
              ...event.payload.obstructions
            }
          }
        });
      });
    }
    case FLOOR_MASTERFORMAT_PROGRESS_LOADED: {
      if (event.payload.scanned?.length || event.payload.baseline?.length || event.payload.current?.length) {
        return updateFloor(floors, event.payload.floorId, (floor) => {
          return new Floor({
            ...floor,
            scannedProjectMasterformatProgresses: event.payload.scanned,
            baselineProjectMasterformatProgresses: event.payload.baseline,
            currentProjectMasterformatProgresses: event.payload.current,
          });
        });
      } else {
        return floors;
      }
    }
    case PLANNED_BUILDING_ELEMENTS_LOADED: {
      // Read the fields straight off the payload rather than constructing a PlannedBuildingElement
      // per element. The constructor merely copies these three across, so this is equivalent — and
      // it used to build a throwaway instance for every element on the floor, inside the same
      // dispatch that the pbe_load_timing `store` figure measures.
      // Seeding from the floor is the one behavioral change here, so it is gated on the
      // bigBimMinimalPayload marker: a big-BIM load arrives one page at a time, so trade codes have
      // to accumulate across pages. An unflagged dispatch carries the whole floor at once and keeps
      // the pre-big-BIM behavior of rebuilding the map from scratch.
      const floor = floors.byFirebaseId[event.payload.floorId];
      const elementTradeCodesInBim = event.payload.bulkIngest ? { ...floor?.elementTradeCodesInBim } : {};
      const codesBefore = Object.keys(elementTradeCodesInBim).length;
      event.payload.elements.forEach((element) => {
        const masterformat = (element as { masterformat?: string }).masterformat;
        if (masterformat != null) {
          elementTradeCodesInBim[masterformat] = true;
        }

        if (element.trade != null) {
          elementTradeCodesInBim[element.trade] = true;
        }

        if (element.uniformat != null) {
          elementTradeCodesInBim[element.uniformat] = true;
        }
      });
      // A page with no new codes (eg: the full payload after the minimal one) leaves the floors as they were,
      // so the floor's selectors don't rerun.
      if (event.payload.bulkIngest && floor && Object.keys(elementTradeCodesInBim).length === codesBefore && isBigBimViewerPerformanceOn()) {
        return floors;
      }
      return updateFloor(floors, event.payload.floorId, existingFloor => ({
        ...existingFloor,
        elementTradeCodesInBim
      }));
    }
    default: {
      return floors;
    }
  }
};

export default reduceFloors;
